import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
      setDebugInfo(prev => `${prev}\nDevice: ${isMobileDevice ? 'Mobile' : 'Desktop'}`);
    };

    checkMobile();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      console.log('beforeinstallprompt event fired');
      setDebugInfo(prev => `${prev}\nInstall prompt available`);
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    // Check if the app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App is already installed');
      setDebugInfo(prev => `${prev}\nApp is already installed`);
      return;
    }

    // Check if the browser supports PWA installation
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      console.log('Browser supports standalone mode');
      setDebugInfo(prev => `${prev}\nBrowser supports standalone mode`);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Debug info
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.matchMedia('(display-mode: window-controls-overlay)').matches;
    
    const debugDetails = {
      isStandalone,
      isPWA,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      displayMode: window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser',
      isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
      isAndroid: /Android/i.test(navigator.userAgent)
    };

    console.log('PWA Debug Info:', debugDetails);
    setDebugInfo(prev => `${prev}\n${JSON.stringify(debugDetails, null, 2)}`);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    try {
      console.log('Installing PWA...');
      setDebugInfo(prev => `${prev}\nInstalling...`);
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Installation outcome:', outcome);
      setDebugInfo(prev => `${prev}\nInstallation ${outcome}`);
      if (outcome === 'accepted') {
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Error installing PWA:', error);
      setDebugInfo(prev => `${prev}\nInstallation error: ${error.message}`);
    }
  };

  // Don't show prompt on iOS as it has a different install flow
  if (isMobile && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
    return null;
  }

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black/50 p-4 z-50">
      <div className="bg-white rounded-xl p-4 flex items-center justify-between shadow-lg">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Install CitaLoo App</h3>
          <p className="text-sm text-gray-600">
            Install our app for a better experience and quick access to your bookings.
          </p>
          {process.env.NODE_ENV === 'development' && debugInfo && (
            <pre className="text-xs text-gray-500 mt-1 whitespace-pre-wrap">
              {debugInfo}
            </pre>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleInstall}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Install Now
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
} 